import { sendGTMEvent } from '@next/third-parties/google';
import { usePathname, useRouter } from 'next/navigation';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTabState } from '@/components/ParticipantTabs/ParticipantTabs.helpers';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { useBelow } from '@/hooks/useBelow';
import { useParticipant } from '@/providers/ParticipantProvider';

import {
  PARTICIPANT_TABS,
  RESUME_DETAILS_REGEX,
  ROUTE_BY_PARTICIPANT,
  VACANCY_DETAILS_REGEX
} from './ParticipantTabs.constants';
import * as S from './ParticipantTabs.styles';

type Props = { isSimplified?: boolean };

export const ParticipantTabs: FC<Props> = ({ isSimplified = false }) => {
  const { setParticipant, loading } = useParticipant();
  const { t } = useTranslation();
  const isMobile = useBelow('tablet');
  const { push } = useRouter();
  const pathname = usePathname() || '';

  const tabs = useMemo(
    () =>
      PARTICIPANT_TABS.map(({ id, participant }) => ({
        id,
        participant
      })),
    []
  );

  const isDetailsPage =
    RESUME_DETAILS_REGEX.test(pathname) || VACANCY_DETAILS_REGEX.test(pathname);

  if (loading) {
    return (
      <S.TabsWrapper
        $isSimplified={isSimplified}
        data-testid="participant-tabs-skeleton"
      >
        <S.TabSkeleton />
        <S.TabSkeleton />
      </S.TabsWrapper>
    );
  }

  return (
    <S.TabsWrapper $isSimplified={isSimplified}>
      {tabs.map(tab => {
        const participantLower = tab.participant.toLowerCase();

        const { isSelected, disabled } = getTabState({
          pathname,
          isDetailsPage,
          participant: tab.participant
        });

        const handleClick = () => {
          if (disabled) return;
          sendGTMEvent({
            event: 'switcher_tab_clicked',
            tab: tab.participant === 'VACANCY' ? 'job_listings' : 'resumes'
          });
          setParticipant(tab.participant);
          push(ROUTE_BY_PARTICIPANT[tab.participant]);
        };

        return (
          <S.Tab
            key={tab.id}
            type="button"
            data-testid={`${isSelected ? 'selected-' : ''}participant-${participantLower}`}
            $isSelected={isSelected}
            $isSimplified={isSimplified}
            onClick={disabled ? undefined : handleClick}
            aria-disabled={disabled}
          >
            <Typography variant={isMobile ? 'title6' : 'title4'}>
              {t(`Header.nav_link_${participantLower}`)}
            </Typography>
          </S.Tab>
        );
      })}
    </S.TabsWrapper>
  );
};
